import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { parseWSResponse, wsActionTypes, WSMessage } from 'shared';
import useAlert from '../alert';
import localStorage from '../../utils/localStorage';
import { nanoid } from 'nanoid';
import WSServiceDialog from './serviceDialog';
import useInitFight from '../initFight';

type WSMessageHandler<T> = (message: WSMessage<T>) => any;

interface IWS {
  wsConnect: () => void;
  wsDisconnect: () => void;
  wsConnected: boolean;
  wsError: boolean;
  wsConnecting: boolean;
  wsDisconnected: boolean;
  sendWSMessage: (message: string) => void;
  addMessageHandler: <T>(handler: WSMessageHandler<T>) => string;
  removeMessageHandler: (id: string) => void;
}

// @ts-ignore
const WSContext = createContext<IWS>();

interface WSProviderProps {
  children: React.ReactNode;
}

export const WSContextProvider = ({ children }: WSProviderProps) => {
  const alert = useAlert();
  const client = useRef<WebSocket | undefined>(undefined);
  const { goHome } = useInitFight();

  //connection management
  const connectionAttempts = useRef<number>(0);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [disconnected, setDisconnected] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<boolean>(false);
  const explicitelyDisconnected = useRef<boolean>(false);

  //BE messages handlers
  const handlers = useRef<{ handler: WSMessageHandler<any>; id: string }[]>([]);

  const explicitDisconnect = () => {
    explicitelyDisconnected.current = true;
    if (client.current) {
      client.current.close(1000, 'Finished the fight');
    }
  };

  const onDisconnect = () => {
    setConnected(false);
    client.current = undefined;
    handlers.current = [];
    if (!explicitelyDisconnected.current) {
      setDisconnected(true);
    }
  };

  useEffect(() => {
    return () => {
      if (client.current) client.current.close(1000, 'Leaving fight page');
    };
  }, []);

  const handleConnect = () => {
    connectionAttempts.current = connectionAttempts.current + 1;
    connect();
  };

  const connect = () => {
    //setting certain statuses
    setConnecting(true);
    setError(false);
    setConnected(false);
    setDisconnected(false);
    explicitelyDisconnected.current = false;

    //connecting
    const host = window.location.host;
    const protocol = process.env.NODE_ENV === 'production' ? 'wss' : 'ws';
    const url = `${protocol}://${host}/fight?token=${localStorage.getToken()}`;
    client.current = new WebSocket(url, 'echo-protocol');

    client.current.onopen = () => {
      setConnecting(false);
      setConnected(true);
      connectionAttempts.current = 0;
    };
    client.current.onerror = () => {
      if (connectionAttempts.current < 3) {
        handleConnect();
      } else {
        setConnecting(false);
        setError(true);
      }
    };
    client.current.onclose = (e) => {
      onDisconnect();
      if (e.code === 4000) {
        alert.showError('Joined the fight from another device!');
      }
    };
    client.current.onmessage = (e) => {
      handleMessageReceive(e.data);
    };
  };

  const addMessageHandler = (handler: WSMessageHandler<any>): string => {
    const id = nanoid(32);
    handlers.current.push({ handler, id });
    return id;
  };

  const removeMessageHandler = (id: string) => {
    handlers.current = handlers.current.filter((h) => h.id !== id);
  };

  const handleMessageReceive = (message: string) => {
    const parsed = parseWSResponse(message);
    if ('error' in parsed) {
      return alert.showError('Impoper server response');
    }
    if (parsed.actionType === wsActionTypes.server.unexpectedError) {
      setUnexpectedError(true);
    }
    handlers.current.forEach((h) => {
      h.handler(parsed);
    });
  };

  const sendWSMessage = (message) => {
    if (client.current) client.current.send(message);
  };

  return (
    <WSContext.Provider
      value={{
        wsConnect: handleConnect,
        wsDisconnect: explicitDisconnect,
        wsConnected: connected,
        wsError: error,
        wsConnecting: connecting,
        wsDisconnected: disconnected,

        //methods
        sendWSMessage,
        addMessageHandler,
        removeMessageHandler
      }}
    >
      {children}
      <WSServiceDialog
        disconnected={disconnected}
        connecting={connecting}
        error={error}
        unexpectedError={unexpectedError}
        reconnect={handleConnect}
        exit={() => {
          explicitDisconnect();
          goHome();
        }}
      />
    </WSContext.Provider>
  );
};

const useWs = () => useContext(WSContext);

export default useWs;
